import { Media } from '@/components/Media'
import { track, trackCta } from '@/lib/analytics'
import { PlayBadge } from '@/components/PlayBadge'
import type { PlatformKey, StudioContent } from '@/lib/content.types'

const PLATFORM_ICONS: Record<PlatformKey, JSX.Element> = {
  macos: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="#C6CBD4" aria-hidden="true">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.032 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  ),
  windows: (
    <svg width="25" height="25" viewBox="0 0 24 24" fill="#C6CBD4" aria-hidden="true">
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
    </svg>
  ),
  web: (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#C6CBD4"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9.2" />
      <ellipse cx="12" cy="12" rx="4.1" ry="9.2" />
      <line x1="2.9" y1="12" x2="21.1" y2="12" />
    </svg>
  ),
}

const GALLERY_CARD =
  'group relative block aspect-video overflow-hidden rounded-md border border-line-media transition-colors sm:rounded-tile'

/**
 * 갤러리 카드.
 *
 * 연결된 HTML을 **새 탭**으로 여는 것은 그대로지만, 그 HTML로 바로 이동하지 않고
 * 우리 래퍼 페이지(`/viewer/:id`)를 열어 iframe 안에 담는다.
 * 덕분에 주소창에 뷰어 파일 경로가 노출되지 않는다 (`pages/Viewer.tsx` 참고).
 *
 * 접근 티켓도 이 시점에는 발급하지 않는다 — 래퍼 페이지가 열릴 때 받으므로
 * 누르지도 않은 카드의 티켓이 랜딩 DOM에 미리 뿌려지지 않는다.
 *
 * href 가 비어 있으면 링크가 아닌 일반 카드로 렌더한다(죽은 링크 방지).
 */
function GalleryCard({
  id,
  index,
  href,
  title,
  children,
}: {
  id: string
  /** 갤러리에서의 순서 (1부터) — 분석용 */
  index: number
  /** 콘텐츠에 저장된 원본 링크 — 비어 있는지 판단하는 용도로만 쓴다 */
  href: string
  title: string
  children: React.ReactNode
}) {
  if (!href.trim()) {
    return <div className={GALLERY_CARD}>{children}</div>
  }

  const reportOpen = (method: string) =>
    track('Viewer Opened', {
      gallery_id: id,
      gallery_title: title,
      gallery_index: index,
      open_method: method,
    })

  return (
    <a
      href={`/viewer/${encodeURIComponent(id)}`}
      target="_blank"
      /**
       * 평범한 클릭은 가로채서 `window.open` 으로 연다.
       *
       * 새 탭의 "← Back to site" 가 원래 탭으로 **돌아가려면** `window.opener` 가 필요한데,
       * 요즘 브라우저는 `target="_blank"` 링크에 `rel` 과 무관하게 noopener를 강제한다.
       * (`rel` 을 비워도 `window.opener` 는 null이다 — 실제로 확인함)
       * `window.open` 으로 열면 opener가 정상적으로 연결된다.
       *
       * 여기서 여는 대상은 남의 사이트가 아니라 우리 래퍼 페이지(`pages/Viewer.tsx`)이고,
       * 정작 외부 콘텐츠는 그 안의 iframe에 cross-origin 으로 들어가 opener에 닿지 못한다.
       * 단, 뷰어 HTML을 **본 사이트와 같은 출처**로 서빙하면 그 iframe이 opener에 닿을 수 있으니
       * `docs/DEPLOY-AWS.md` 가 권하는 대로 `/pages/*` 를 별도 도메인으로 분리할 것.
       *
       * href는 그대로 두어 가운데 클릭·"새 탭에서 열기"·크롤러가 정상 동작하게 한다.
       * 그 경로로 열린 탭은 opener가 없지만, 래퍼가 랜딩페이지 이동으로 폴백한다.
       */
      // 가운데 버튼 클릭은 onClick 이 아니라 auxclick 으로 온다
      onAuxClick={(e) => {
        if (e.button === 1) reportOpen('middle_click')
      }}
      onClick={(e) => {
        reportOpen(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ? 'modifier_click' : 'click')
        // 보조키를 누른 클릭(새 창, 백그라운드 탭 등)은 브라우저에 맡긴다
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
        e.preventDefault()
        window.open(e.currentTarget.href, '_blank')
      }}
      aria-label={title || undefined}
      className={`${GALLERY_CARD} cursor-pointer hover:border-blue-brand`}
    >
      {children}
      {/* 새 탭으로 열린다는 것을 시각적으로 알리는 표식 */}
      <span
        aria-hidden="true"
        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md bg-surface-base/70 text-[11px] text-ink-secondary opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100"
      >
        ↗
      </span>
    </a>
  )
}

export function Studio({ data }: { data: StudioContent }) {
  return (
    // 섹션 순서가 Hub → Studio → VFX 로 바뀌면서
    // 밝기 교차 밴드(base ↔ alt)를 유지하기 위해 Studio가 alt 밴드를 맡는다.
    <section
      id="studio"
      className="relative border-y border-line-hairline bg-surface-alt px-5 py-20 md:px-10 md:py-[104px]"
    >
      {/* 모바일에서는 전체 중앙 정렬, md 이상에서는 디자인대로 좌측 정렬 */}
      <div className="mx-auto max-w-content text-center md:text-left">
        <div className="mb-3.5 flex flex-col items-center gap-5 md:flex-row md:flex-wrap md:items-end md:justify-between">
          <div>
            <div className="annx-eyebrow mb-3.5">{data.eyebrow}</div>
            <Media
              media={data.logo}
              className="annx-section-logo mb-3.5"
            />
            <p className="annx-deck mx-auto max-w-[560px] md:mx-0">{data.deck}</p>
          </div>
          <a
            href={data.learnMore.href}
            onClick={() => trackCta('studio_learn_more', data.learnMore.label, data.learnMore.href)}
            className="annx-learn-more"
          >
            {data.learnMore.label}
          </a>
        </div>

        {/* 지원 플랫폼 */}
        <ul className="my-[26px] mb-[34px] flex list-none items-start justify-center gap-[30px] p-0 md:justify-start">
          {data.platforms.map((p) => (
            <li
              key={p.key}
              className="flex flex-col items-center gap-[9px]"
              style={{ opacity: p.dimmed ? 0.5 : 1 }}
            >
              {PLATFORM_ICONS[p.key]}
              <span className="text-xs font-semibold text-ink-secondary">{p.label}</span>
            </li>
          ))}
        </ul>

        {/* 사용 영상 */}
        <div className="annx-media-frame mb-4 aspect-video">
          <Media media={data.usage} />
          {data.usage.kind === 'image' && <PlayBadge size={58} />}
        </div>

        {/* 결과물 갤러리 — 항상 3열. 사용 영상과 같은 폭 안에서
            화면 크기에 맞춰 카드가 같이 줄어든다(모바일에서는 작은 썸네일 3개).
            카드가 4개 이상이면 자연스럽게 다음 줄로 넘어간다. */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
          {data.gallery.map((item, i) => (
            <GalleryCard key={item.id} id={item.id} index={i + 1} href={item.href} title={item.title}>
              <Media media={item.media} />
            </GalleryCard>
          ))}
        </div>

        {/* 카드를 누를 수 있다는 안내 — 비우면 그리지 않는다.
            `?.` 를 붙인 이유: API가 꺼져 있으면 저장소에 커밋된 seed JSON 으로 폴백하는데,
            그 파일이 스키마보다 오래돼 이 필드가 없을 수 있다. 그때 .trim() 이 터지면
            화면 전체가 하얗게 된다. 새 필드를 쓸 때는 항상 이렇게 접근할 것. */}
        {data.galleryNote?.trim() && (
          <p className="mt-3.5 text-center text-[13px] text-ink-tertiary">{data.galleryNote}</p>
        )}
      </div>
    </section>
  )
}
